import { RecurrenceType } from "../models/enums/recurrence-type.enum";
import { RecurrenceUnit } from "../models/enums/recurrence-unit.enum";
import { BaseData } from "@app/maintenance/models/logical-grouper/base-data.model";
import { SelectionData } from "@app/maintenance/models/logical-grouper/selection-data.model";
import { Activity } from "../models/activity.model";
import { Recurrence } from "../models/recurrence.model";
import { ActivityTask } from "../models/activity-task.model";

export class MaintenancePlanUtils {

    static mapRecurrenceCard(recurrence: any, subtitleTranslation: any, selectable = true, isMandatory = false) {
        const value = recurrence.type === RecurrenceType.RETURN || recurrence.type === RecurrenceType.ONDEMAND
                        ? ''
                        : recurrence.value;
        const timeUnits = ['', RecurrenceUnit.DAYS, RecurrenceUnit.WEEKS, RecurrenceUnit.MONTHS];
        const unit = ['', 'Return', timeUnits[recurrence.unit], 'Cycles', 'On Demand'][recurrence.type];
        const subtitle = `${subtitleTranslation} ${value} ${unit}`;
        const icon = ['', 'fa fa-undo', 'fa fa-clock-o', 'fa fa-refresh', 'fa fa-asterisk'][recurrence.type];

        return {
          id: recurrence.id,
          title: recurrence.name,
          subtitle: subtitle,
          icon: icon,
          type: recurrence.type,
          value: value,
          unit: unit,
          baseData: new BaseData({
            title: recurrence.name + (isMandatory ? ' - Mandatory' : ''),
            subtitle: subtitle,
            icon: icon
          }),
          selectionData: new SelectionData({
            isSelectable: selectable,
            isSelected: recurrence.type === RecurrenceType.RETURN || isMandatory,
            isMandatory: isMandatory
          })
        };
    }

    static mapRecurrence(recurrence) {
        const copyRecurrence: any = {
          id: recurrence.id,
          name: recurrence.name,
          type: recurrence.type,
          value: (recurrence.value
              && (recurrence.type === RecurrenceType.TIME || recurrence.type === RecurrenceType.CYCLE))
              ? recurrence.value : 0,

          unit: (recurrence.unit && recurrence.type === RecurrenceType.TIME) ? recurrence.unit : RecurrenceUnit.EMPTY,
        };

        return recurrence ? new Recurrence(copyRecurrence) : new Recurrence();
    }

    static groupMaintenancePlanItems(maintenancePlanItems, mandatoryMaintenancePlanItems, selectedOperations, subtitleTranslation) {
        const operations = [];
        const mergedItems = this.mergeMaintenancePlanItems(maintenancePlanItems, mandatoryMaintenancePlanItems);

        mergedItems.forEach((mpi) => {
            if (selectedOperations.includes(mpi.operationId)) {
                const operationIndex = operations.findIndex((operation) => operation.id === mpi.operationId);

                const recurrence = MaintenancePlanUtils.mapRecurrenceCard(
                    new Recurrence(mpi.recurrence),
                    subtitleTranslation,
                    true,
                    mpi.isMandatory
                );

                if (operationIndex === -1) {
                    operations.push(mpi.operation);
                    operations[operations.length - 1].selectedItems = [recurrence];
                    operations[operations.length - 1].items = [recurrence];
                    operations[operations.length - 1].expanded = true;
                } else {
                    if (!operations[operationIndex].selectedItems.some((rec) => rec.id === mpi.recurrenceId)) {
                        if (recurrence.type === RecurrenceType.RETURN) {
                            operations[operationIndex].selectedItems.unshift(recurrence);
                            operations[operationIndex].items.unshift(recurrence);
                        } else {
                            operations[operationIndex].selectedItems.push(recurrence);
                            operations[operationIndex].items.push(recurrence);
                        }
                    }
                }
            }
        })

        return operations;
    }

    static mergeMaintenancePlanItems(maintenancePlanItems, mandatoryMaintenancePlanItems): any[] {
        const combinedLists = [...maintenancePlanItems, ...mandatoryMaintenancePlanItems];

        const itemsMap = combinedLists.reduce((map, item) => {
            const key = `${item.id}-${item.recurrenceId}-${item.operationId}-${item.operationTaskId}`;
            if (!map.has(key) || item.isMandatory) {
                map.set(key, item);
            }
            return map;
        }, new Map<string, any>());

        return Array.from(itemsMap.values());
    }

    static computeOperationTypes(controlValues: any[], mandatoryPlanItems: any[], operationTypes: any[]) {
        const operations = controlValues;

        if (mandatoryPlanItems.length === 0) {
            return controlValues;
        }

        mandatoryPlanItems.forEach((item) => {
            const operationIndex = operations.find(operation => operation.id === item.operationId);
            if (!operationIndex) {
                const operationType = operationTypes.find(type => type.id === item.operationId);
                operations.push(operationType)
            }
        })
        return operations;
    }

    static groupTools(tools) {
        const groupedTools = [];

        tools.forEach((tool) => {
            const maintenancePlanId = tool.maintenancePlanId ? tool.maintenancePlanId : tool.data.maintenancePlanId;
            const toolId = tool.id ? tool.id : tool.data.id;

            if (maintenancePlanId) {
                const toolIndex = groupedTools.findIndex((toolGroup) => toolGroup.maintenancePlanId === maintenancePlanId);
                if (toolIndex === -1) {
                    groupedTools.push({maintenancePlanId: maintenancePlanId, ids: [toolId]});
                } else {
                    groupedTools[toolIndex].ids.push(toolId);
                }
            }
        })

        return groupedTools;
    }

    static groupSelectedOperations(operations) {
        const selectedOperations = [];

        operations.forEach((operation) => {
            operation.items.forEach((recurrence) => {
                if (recurrence.selectionData.isSelected) {
                    const operationIndex = selectedOperations.findIndex((op) => op.id === operation.id);
                    if (operationIndex === -1 ) {
                        selectedOperations.push({id: operation.id, recurrenceList: [recurrence.id]});
                    } else {
                        selectedOperations[operationIndex].recurrenceList.push(recurrence.id);
                    }
                }
            })
        });

        return selectedOperations;
    }

    static computeTicketOperations(groupedTools, groupedOperations, maintenancePlanItems) {
        const ticketOperations = [];

        groupedTools.forEach((toolGroup) => {

            groupedOperations.forEach((operationPair) => {

                if (!ticketOperations.some((ticketOperation) =>
                                        ticketOperation.operationId === operationPair.id &&
                                        (ticketOperation.recurrenceIds.sort() + "" === operationPair.recurrenceList + "") &&
                                        (ticketOperation.toolIds.sort() + "" === toolGroup.ids.sort() + ""))) {

                    const taskList = maintenancePlanItems
                                         .filter((mpi) => mpi.maintenancePlanId === toolGroup.maintenancePlanId &&
                                                          mpi.operationId === operationPair.id &&
                                                          operationPair.recurrenceList.includes(mpi.recurrenceId))
                                         .map((mpi) => mpi.operationTaskId);  // get task list for planId, operationId, recurrenceIds
                    if (Array.isArray(taskList) && taskList.length !== 0) {
                        ticketOperations.push({
                            operationId: operationPair.id,
                            recurrenceIds: operationPair.recurrenceList,
                            toolIds: toolGroup.ids,
                            operationTaskIds: taskList
                        })
                    }
                }
            })
        });

        return ticketOperations;
    }

    static buildOverviewItems(groupedTools,
                              groupedOperations,
                              tools,
                              maintenancePlanItems,
                              subtitleTranslation) {
        groupedTools = groupedTools.map((group) => {
            const toolNames = Array.from(new Set(group.ids.map((toolId) => {
                const tool = tools.find((t) => (t.id === toolId || t.data.id === toolId));
                const name = tool.data ? tool.data.name : tool.name;
                return tool ? name : '';
            })));


            return { ...group, toolNames };
        })

        const overviewItems = groupedTools.map(group => {
            const matchingItems = maintenancePlanItems.filter(item =>
                item.maintenancePlanId === group.maintenancePlanId
                && groupedOperations.some(pair =>
                    pair.id === item.operationId
                    && pair.recurrenceList.includes(item.recurrenceId)));

            const operations = [];


            matchingItems.forEach(item => {
                let index = operations.findIndex(operation => item.operation.id === operation.id);
                if (index < 0) {
                    operations.push(new Activity(item.operation));
                    index = operations.length - 1;
                    operations[index].selected = true;
                    operations[index].expanded = true;
                }

                if (!operations[index].selectedItems) {
                    operations[index].selectedItems = [];
                    operations[index].items = [];
                }

                let recurrenceIndex = operations[index].selectedItems.findIndex(recurrence => item.recurrence.id === recurrence.id);
                if (recurrenceIndex < 0) {
                    operations[index].selectedItems.push(MaintenancePlanUtils.mapRecurrenceCard(
                        new Recurrence(item.recurrence),
                        subtitleTranslation
                    ));
                    recurrenceIndex = operations[index].selectedItems.length - 1;
                }

                if (!operations[index].selectedItems[recurrenceIndex].selectedTasks) {
                    operations[index].selectedItems[recurrenceIndex].selectedTasks = [];
                    operations[index].selectedItems[recurrenceIndex].tasks = [];
                }

                operations[index].selectedItems[recurrenceIndex].selectedTasks.push(new ActivityTask(item.operationTask));
            })

            return { ...group, items: operations };
        })

        return overviewItems;
    }

    static buildPlanDetailsItems(items: any[], subtitleTranslation: string) {
        const activities = [];

        items.forEach(item => {
          // add activity if missing
          let index = activities.findIndex(activity => item.activity.id === activity.id);
          if (index < 0) {
            activities.push(new Activity(item.activity));
            index = activities.length - 1;
            activities[index].selected = true;
            activities[index].expanded = true;
          }

          // create list of recurrences if missing
          if (!activities[index].selectedItems) {
            activities[index].selectedItems = [];
            activities[index].items = [];
          }

          // add recurrence if missing
          let recurrenceIndex = activities[index].selectedItems.findIndex(recurrence => item.recurrence.id === recurrence.id);
          if (recurrenceIndex < 0) {
            const recurrence = MaintenancePlanUtils.mapRecurrenceCard(item.recurrence, subtitleTranslation, false);
            if (recurrence.type === RecurrenceType.RETURN) {
              activities[index].selectedItems.unshift(recurrence);
            } else {
              activities[index].selectedItems.push(recurrence);
            }
            recurrenceIndex = activities[index].selectedItems.length - 1;
          }

          // create list of tasks if missing
          if (!activities[index].selectedItems[recurrenceIndex].selectedTasks) {
            activities[index].selectedItems[recurrenceIndex].selectedTasks = [];
            activities[index].selectedItems[recurrenceIndex].tasks = [];
          }

          // add task
          activities[index].selectedItems[recurrenceIndex].selectedTasks.push(new ActivityTask(item.activityTask));
        });

        return activities;
    }
}
